import { ApiError } from '@l2u/web-shared';

// Where a refused portal request should send the user.
//
// `account_locked` (403) is the api's answer for an account whose compliance
// questionnaire passed its deadline unsubmitted. It is thrown only once
// identity is proven — a verified sign-in code, a signed invitation token, or
// a live session — so reaching the /locked page never tells anyone whether an
// address exists. Every other refusal keeps the page it already had.

/** Did the api refuse this request because the account is locked? */
export function isAccountLocked(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'account_locked';
}

/**
 * The shell's redirect for its identity query failing, or null to stay put.
 *
 * The lock is checked FIRST, and that order is the point: `account_locked` is
 * a 403, and the driver shell has always sent EVERY 403 to /expired ("your
 * sign-in link has expired" — wrong copy for a lock, and its one button would
 * email a fresh link straight back into the same refusal). After it, each
 * shell keeps its existing rule: the company shell reacts to a 401 only; the
 * driver shell to a 401 or a 403.
 */
export function shellRedirectFor(
  err: unknown,
  kind: 'company' | 'driver',
): '/locked' | '/expired' | null {
  if (isAccountLocked(err)) return '/locked';
  if (!(err instanceof ApiError)) return null;
  if (err.status === 401) return '/expired';
  if (kind === 'driver' && err.status === 403) return '/expired';
  return null;
}

/**
 * The redirect for the FIRST of the shell's own queries that warrants one, or
 * null. Every one of them passes the same auth middleware, so whichever lands
 * first — /me, nav-counts, the badge count — is the earliest news that the
 * session is lapsed or the account locked; waiting for /me alone left a frame
 * in which the page showed its own 403 error state instead.
 */
export function shellRedirectForAny(
  errors: readonly unknown[],
  kind: 'company' | 'driver',
): '/locked' | '/expired' | null {
  for (const err of errors) {
    const to = shellRedirectFor(err, kind);
    if (to) return to;
  }
  return null;
}
